// Documents, Messages, Activity, Tax Report, Settings pages
// SECURITY: All dynamic content escaped via esc() before any innerHTML assignment
import { api } from '../api';
import { state, isAdmin, navigate } from '../app';
import { esc, $, $$, setHTML, formatCurrency, formatDate, statusBadge, timeAgo, icons } from '../helpers';
import { toast, openModal, closeModal, setBreadcrumb, skeletonTable } from '../ui';
// Note: Messages removed — replaced by tickets system (see tickets.ts)

// ─── Documents ──────────────────────────────────────────────────────────────
export async function renderDocuments() {
  const content = $('#content')!;
  setBreadcrumb([{ label: 'Dashboard', href: '/dashboard' }, { label: 'Documents' }]);
  setHTML(content, skeletonTable(5, 4));
  try {
    const data = await api.get('/documents');
    const docs = data.documents || [];
    let rows = '';
    docs.forEach((d: any) => {
      rows += '<tr><td>' + esc(d.name) + '</td><td>' + statusBadge(d.type) + '</td>'
        + (isAdmin() ? '<td>' + esc(d.client_name || '\u2014') + '</td>' : '')
        + '<td>' + esc(d.project_title || '\u2014') + '</td><td>' + formatDate(d.created_at) + '</td></tr>';
    });
    const tableHtml = !docs.length
      ? '<div class="empty-state"><p>No documents yet</p></div>'
      : '<div class="table-wrapper"><table><thead><tr><th>Name</th><th>Type</th>' + (isAdmin() ? '<th>Client</th>' : '') + '<th>Project</th><th>Date</th></tr></thead><tbody>' + rows + '</tbody></table></div>';
    // SAFE: all dynamic values in rows are escaped via esc()/statusBadge()/formatDate()
    setHTML(content, '<div class="page-enter"><div class="page-header"><h1>Documents</h1>'
      + (isAdmin() ? '<div class="page-header-actions"><button class="btn btn-primary" id="add-doc-btn"><i data-lucide="plus"></i> Add Document</button></div>' : '')
      + '</div><div class="card"><div class="card-body no-pad">' + tableHtml + '</div></div></div>');
    icons();
    document.getElementById('add-doc-btn')?.addEventListener('click', async () => {
      let cOpts = '', pOpts = '';
      if (isAdmin()) { try { const [c,p] = await Promise.all([api.get('/clients'),api.get('/projects')]); cOpts = (c.clients||[]).map((x:any)=>'<option value="'+x.id+'">'+esc(x.name)+'</option>').join(''); pOpts = (p.projects||[]).map((x:any)=>'<option value="'+x.id+'">'+esc(x.title)+'</option>').join(''); } catch{} }
      // SAFE: cOpts/pOpts escaped via esc()
      openModal('Add Document', '<form id="doc-form"><div class="form-group"><label>Name *</label><input type="text" name="name" required></div>'
        + '<div class="form-group"><label>Type *</label><select name="type" required><option value="contract">Contract</option><option value="proposal">Proposal</option><option value="deliverable">Deliverable</option><option value="id_document">ID Document</option><option value="receipt">Receipt</option><option value="other">Other</option></select></div>'
        + (isAdmin() ? '<div class="form-row"><div class="form-group"><label>Client</label><select name="client_id"><option value="">--</option>'+cOpts+'</select></div><div class="form-group"><label>Project</label><select name="project_id"><option value="">--</option>'+pOpts+'</select></div></div>' : '')
        + '<div class="form-group"><label>Notes</label><textarea name="notes" rows="2"></textarea></div>'
        + '<div class="modal-footer" style="padding:1rem 0 0"><button type="button" class="btn btn-secondary" id="doc-cancel">Cancel</button><button type="submit" class="btn btn-primary">Add</button></div></form>');
      document.getElementById('doc-cancel')?.addEventListener('click', closeModal);
      document.getElementById('doc-form')?.addEventListener('submit', async (e) => { e.preventDefault(); const fd = new FormData(e.target as HTMLFormElement); const body: any = Object.fromEntries(fd); if(body.client_id) body.client_id=parseInt(body.client_id); else delete body.client_id; if(body.project_id) body.project_id=parseInt(body.project_id); else delete body.project_id; try { await api.post('/documents', body); toast('Document added','success'); closeModal(); navigate('/documents'); } catch(err:any){toast(err.message,'error');} });
    });
  } catch (err: any) { toast(err.message, 'error'); }
}

// ─── Activity Log ───────────────────────────────────────────────────────────
export async function renderActivity() {
  if(!isAdmin()) return navigate('/dashboard');
  const content = $('#content')!;
  setBreadcrumb([{label:'Dashboard',href:'/dashboard'},{label:'Activity Log'}]);
  setHTML(content, skeletonTable(6,8));
  try {
    const data = await api.get('/activity');
    const logs = data.activity || [];
    let rows = '';
    logs.forEach((l:any) => { rows += '<tr><td>'+formatDate(l.created_at)+'<br><span class="text-muted" style="font-size:0.78rem">'+timeAgo(l.created_at)+'</span></td><td>'+esc(l.user_name||'System')+'</td><td>'+statusBadge(l.action)+'</td><td>'+esc(l.entity_type)+' #'+esc(l.entity_id)+'</td><td class="font-mono" style="max-width:200px;overflow:hidden;text-overflow:ellipsis">'+esc(l.details||'\u2014')+'</td><td><span class="font-mono" style="color:var(--accent)">'+esc(l.ip_address||'\u2014')+'</span></td></tr>'; });
    const tableHtml = !logs.length ? '<div class="empty-state"><p>No activity</p></div>' : '<div class="table-wrapper"><table><thead><tr><th>Time</th><th>User</th><th>Action</th><th>Entity</th><th>Details</th><th>IP</th></tr></thead><tbody>'+rows+'</tbody></table></div>';
    // SAFE: rows escaped via esc()
    setHTML(content, '<div class="page-enter"><div class="page-header"><h1>Activity Log</h1></div><div class="card"><div class="card-body no-pad">'+tableHtml+'</div></div></div>');
  } catch(err:any) { toast(err.message,'error'); }
}

// ─── Tax Report ─────────────────────────────────────────────────────────────
export async function renderTaxReport() {
  if(!isAdmin()) return navigate('/dashboard');
  const content = $('#content')!;
  setBreadcrumb([{label:'Dashboard',href:'/dashboard'},{label:'Tax Report'}]);
  const year = new Date().getFullYear();
  // SAFE: year is numeric, options are static HTML
  setHTML(content, '<div class="page-enter"><div class="page-header"><h1>Tax Report</h1><div class="page-header-actions">'
    + '<select id="tax-year" class="btn btn-secondary" style="padding:0.5rem 1rem">'+[year,year-1,year-2].map(y=>'<option value="'+y+'">'+y+'</option>').join('')+'</select>'
    + '<button class="btn btn-primary" id="gen-tax-btn"><i data-lucide="file-down"></i> Generate</button>'
    + '<button class="btn btn-secondary" id="export-pdf-btn" style="display:none"><i data-lucide="file-text"></i> PDF</button>'
    + '<button class="btn btn-secondary" id="export-csv-btn" style="display:none"><i data-lucide="download"></i> CSV</button>'
    + '</div></div>'
    + '<div id="tax-report-content"><div class="empty-state"><p>Select a year and click Generate</p></div></div></div>');
  icons();
  document.getElementById('gen-tax-btn')?.addEventListener('click', genTax);
}

function csvSafe(v: any): string {
  const s = String(v ?? '').replace(/"/g, '""');
  return /^[=+\-@\t\r]/.test(s) ? "\"'" + s + '"' : '"' + s + '"';
}

function buildTaxReportHTML(data: any, year: string): string {
  const s = data.summary;
  const q = s.quarterly || [0,0,0,0];

  // SAFE: all dynamic values pass through esc()/formatCurrency()/formatDate()/statusBadge()
  let cR = '';
  (data.clients||[]).forEach((c:any) => {
    const typeBadge = c.type === 'business'
      ? '<span style="background:var(--info-soft);color:var(--info);padding:2px 8px;border-radius:4px;font-size:0.75rem;font-weight:600">Business</span>'
      : '<span style="background:var(--accent-soft);color:var(--accent);padding:2px 8px;border-radius:4px;font-size:0.75rem;font-weight:600">Individual</span>';
    cR += '<tr><td>'+esc(c.name)+'</td><td>'+esc(c.company||'\u2014')+'</td><td>'+typeBadge+'</td><td>'+esc(c.country||'\u2014')+'</td><td class="font-mono">'+esc(c.tax_id||c.vat_number||'\u2014')+'</td><td>'+esc(c.invoice_count)+'</td><td>'+formatCurrency(c.total_paid)+'</td></tr>';
  });

  let iR = '';
  (data.invoices||[]).forEach((i:any) => {
    const typeBadge = i.client_type === 'business' ? '<span style="font-size:0.7rem;color:var(--info)">B</span>' : '<span style="font-size:0.7rem;color:var(--accent)">I</span>';
    iR += '<tr><td class="font-mono">'+esc(i.invoice_number)+'</td><td>'+esc(i.client_name)+' '+typeBadge+'</td><td>'+esc(i.client_country||'\u2014')+'</td><td>'+statusBadge(i.status)+'</td><td>'+formatCurrency(i.total,i.currency)+'</td><td>'+formatDate(i.issue_date)+'</td><td>'+formatDate(i.paid_at)+'</td></tr>';
  });

  let pR = '';
  (data.payments||[]).forEach((p:any) => {
    pR += '<tr><td class="font-mono">'+esc(p.invoice_number)+'</td><td>'+esc(p.client_name)+'</td><td>'+esc(p.client_country||'\u2014')+'</td><td>'+formatCurrency(p.amount_usd)+'</td><td>'+(p.amount_crypto?esc(p.amount_crypto)+' '+esc(p.crypto_currency||'USDT'):'\u2014')+'</td><td>'+statusBadge(p.status)+'</td><td>'+formatDate(p.created_at)+'</td></tr>';
  });

  return '<div class="stats-grid">'
    + '<div class="stat-card"><div class="stat-label">Gross Revenue</div><div class="stat-value">'+formatCurrency(s.total_revenue)+'</div></div>'
    + '<div class="stat-card"><div class="stat-label">Domestic (US)</div><div class="stat-value">'+formatCurrency(s.domestic_revenue)+'</div></div>'
    + '<div class="stat-card"><div class="stat-label">International</div><div class="stat-value">'+formatCurrency(s.international_revenue)+'</div></div>'
    + '<div class="stat-card"><div class="stat-label">Stablecoins Received</div><div class="stat-value">'+formatCurrency(s.total_crypto_received)+'</div></div>'
    + '</div>'
    + '<div class="card mt-2"><div class="card-header"><h3>Quarterly Breakdown \u2014 '+esc(year)+'</h3></div><div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr><th>Quarter</th><th>Period</th><th>Revenue</th><th>% of Total</th></tr></thead><tbody>'
    + ['Q1 (Jan\u2013Mar)','Q2 (Apr\u2013Jun)','Q3 (Jul\u2013Sep)','Q4 (Oct\u2013Dec)'].map((label,i) => '<tr><td><strong>'+label+'</strong></td><td>'+esc(year)+'-'+(i*3+1).toString().padStart(2,'0')+' to '+esc(year)+'-'+((i+1)*3).toString().padStart(2,'0')+'</td><td>'+formatCurrency(q[i])+'</td><td>'+(s.total_revenue ? Math.round(q[i]/s.total_revenue*100) : 0)+'%</td></tr>').join('')
    + '<tr style="font-weight:700;border-top:2px solid var(--border)"><td>Total</td><td></td><td>'+formatCurrency(s.total_revenue)+'</td><td>100%</td></tr>'
    + '</tbody></table></div></div></div>'
    + '<div class="card mt-2"><div class="card-header"><h3>Client Summary</h3></div><div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr><th>Name</th><th>Company</th><th>Type</th><th>Country</th><th>Tax ID / VAT</th><th>Invoices</th><th>Paid Total</th></tr></thead><tbody>'+cR+'</tbody></table></div></div></div>'
    + '<div class="card mt-2"><div class="card-header"><h3>Invoices \u2014 '+esc(year)+'</h3></div><div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr><th>Invoice</th><th>Client</th><th>Country</th><th>Status</th><th>Total</th><th>Issued</th><th>Paid</th></tr></thead><tbody>'+iR+'</tbody></table></div></div></div>'
    + '<div class="card mt-2"><div class="card-header"><h3>Payments \u2014 '+esc(year)+'</h3></div><div class="card-body no-pad"><div class="table-wrapper"><table><thead><tr><th>Invoice</th><th>Client</th><th>Country</th><th>USD</th><th>Stablecoins</th><th>Status</th><th>Date</th></tr></thead><tbody>'+pR+'</tbody></table></div></div></div>'
    + buildStablecoinComplianceHTML(data, year);
}

function buildStablecoinComplianceHTML(data: any, year: string): string {
  const sc = data.stablecoin_summary || {};

  // FMV Records table
  let fmvR = '';
  (data.fmv_records||[]).forEach((r:any) => {
    fmvR += '<tr><td class="font-mono">'+esc(r.invoice_number||'\u2014')+'</td><td>'+esc(r.client_name||'\u2014')+'</td><td>'+esc(r.crypto_currency||'\u2014')+'</td><td class="font-mono">'+esc(r.amount_crypto != null ? String(r.amount_crypto) : '\u2014')+'</td><td>'+formatCurrency(r.fmv_usd_per_unit)+'</td><td>'+formatCurrency(r.fmv_total_usd)+'</td><td>'+esc(r.source||'\u2014')+'</td><td>'+formatDate(r.fetched_at)+'</td></tr>';
  });

  // Form 8300 Filing Tracker table
  let f8R = '';
  (data.form8300_filings||[]).forEach((f:any) => {
    f8R += '<tr><td>'+esc(f.client_name||'\u2014')+'</td><td>'+formatCurrency(f.trigger_amount_usd)+'</td><td>'+formatDate(f.trigger_date)+'</td><td>'+formatDate(f.filing_deadline)+'</td><td>'+statusBadge(f.status||'pending')+'</td></tr>';
  });

  return '<div class="card mt-2"><div class="card-header"><h3>Stablecoin Compliance \u2014 '+esc(year)+'</h3></div><div class="card-body">'
    + '<div class="stats-grid" style="margin-bottom:1.5rem">'
    + '<div class="stat-card"><div class="stat-label">Total USDT Received</div><div class="stat-value">'+formatCurrency(sc.total_usdt_received||0)+'</div></div>'
    + '<div class="stat-card"><div class="stat-label">Total USDC Received</div><div class="stat-value">'+formatCurrency(sc.total_usdc_received||0)+'</div></div>'
    + '<div class="stat-card"><div class="stat-label">Total FMV at Receipt</div><div class="stat-value">'+formatCurrency(sc.total_fmv_at_receipt||0)+'</div></div>'
    + '</div>'
    + '<h4 style="margin:1rem 0 0.5rem">FMV Records</h4>'
    + (!(data.fmv_records||[]).length ? '<p class="text-muted">No FMV records for this year</p>'
      : '<div class="table-wrapper"><table><thead><tr><th>Invoice</th><th>Client</th><th>Currency</th><th>Amount</th><th>FMV/Unit</th><th>FMV Total</th><th>Source</th><th>Date</th></tr></thead><tbody>'+fmvR+'</tbody></table></div>')
    + '<h4 style="margin:1.5rem 0 0.5rem">Form 8300 Filing Tracker</h4>'
    + (!(data.form8300_filings||[]).length ? '<p class="text-muted">No Form 8300 filings for this year</p>'
      : '<div class="table-wrapper"><table><thead><tr><th>Client</th><th>Amount</th><th>Trigger Date</th><th>Deadline</th><th>Status</th></tr></thead><tbody>'+f8R+'</tbody></table></div>')
    + '</div></div>';
}

async function genTax() {
  const year = (document.getElementById('tax-year') as HTMLSelectElement).value;
  try {
    const data = await api.get('/export/tax/'+year);
    const el = document.getElementById('tax-report-content')!;
    // SAFE: buildTaxReportHTML escapes all dynamic content via esc()/formatCurrency()/formatDate()/statusBadge()
    setHTML(el, buildTaxReportHTML(data, year));
    icons();

    // Show export buttons in header
    const pdfBtn = document.getElementById('export-pdf-btn') as HTMLElement;
    const csvBtn = document.getElementById('export-csv-btn') as HTMLElement;
    if (pdfBtn) pdfBtn.style.display = '';
    if (csvBtn) csvBtn.style.display = '';

    // CSV Export
    const newCsvBtn = csvBtn.cloneNode(true) as HTMLElement;
    csvBtn.parentNode!.replaceChild(newCsvBtn, csvBtn);
    newCsvBtn.style.display = '';
    newCsvBtn.id = 'export-csv-btn';
    newCsvBtn.addEventListener('click', () => {
      let csv = 'Invoice,Client,Company,Type,Country,Tax ID,Status,Total,Currency,Issued,Paid\n';
      (data.invoices||[]).forEach((i:any) => {
        csv += csvSafe(i.invoice_number)+','+csvSafe(i.client_name)+','+csvSafe(i.client_company)+','+csvSafe(i.client_type)+','+csvSafe(i.client_country)+','+csvSafe(i.client_tax_id||i.client_vat)+','+csvSafe(i.status)+','+csvSafe(i.total)+','+csvSafe(i.currency)+','+csvSafe(i.issue_date)+','+csvSafe(i.paid_at)+'\n';
      });
      const blob = new Blob([csv], {type:'text/csv'});
      const u = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = u; a.download = 'tax-report-'+year+'.csv'; a.click();
      URL.revokeObjectURL(u); toast('CSV exported', 'success');
    });

    // PDF Export
    const newPdfBtn = pdfBtn.cloneNode(true) as HTMLElement;
    pdfBtn.parentNode!.replaceChild(newPdfBtn, pdfBtn);
    newPdfBtn.style.display = '';
    newPdfBtn.id = 'export-pdf-btn';
    newPdfBtn.addEventListener('click', () => { generateTaxPDF(data); });
  } catch(err:any) { toast(err.message,'error'); }
}

function generateTaxPDF(data: any) {
  const jspdfLib = (window as any).jspdf;
  if (!jspdfLib) { toast('PDF library not loaded','error'); return; }
  const doc = new jspdfLib.jsPDF();
  const pageW = doc.internal.pageSize.getWidth();
  const s = data.summary;
  const q = s.quarterly || [0,0,0,0];
  let y = 20;

  // Header
  doc.setFontSize(20); doc.setFont('helvetica','bold'); doc.setTextColor(184,150,12);
  doc.text('Gravant Labs LLC', 20, y);
  doc.setFontSize(8.5); doc.setFont('helvetica','normal'); doc.setTextColor(100,100,100);
  doc.text('1201 Orange Street, Suite 600, Wilmington, DE 19801', 20, y+7);
  doc.text('EIN: 35-2952235', 20, y+12);
  doc.setFontSize(16); doc.setFont('helvetica','bold'); doc.setTextColor(30,30,30);
  doc.text('Tax Report \u2014 ' + data.year, pageW-20, y, {align:'right'});
  doc.setFontSize(8.5); doc.setFont('helvetica','normal'); doc.setTextColor(100,100,100);
  doc.text('Generated: ' + new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'}), pageW-20, y+7, {align:'right'});
  y += 22; doc.setDrawColor(220,220,220); doc.line(20, y, pageW-20, y); y += 10;

  // Income Summary
  doc.setFontSize(12); doc.setFont('helvetica','bold'); doc.setTextColor(30,30,30);
  doc.text('Income Summary', 20, y); y += 8;
  const summaryRows = [
    ['Gross Revenue (Schedule C, Line 1)', formatCurrency(s.total_revenue)],
    ['Domestic Revenue (US)', formatCurrency(s.domestic_revenue)],
    ['International Revenue', formatCurrency(s.international_revenue)],
    ['Stablecoins Received', formatCurrency(s.total_crypto_received)],
    ['Total Invoices', String(s.total_invoices)],
    ['Paid Invoices', String(s.paid_invoices)],
  ];
  doc.setFontSize(9); doc.setFont('helvetica','normal');
  summaryRows.forEach(([label, val]) => {
    doc.setTextColor(80,80,80); doc.text(label, 25, y);
    doc.setTextColor(30,30,30); doc.setFont('helvetica','bold');
    doc.text(val, pageW-25, y, {align:'right'});
    doc.setFont('helvetica','normal'); y += 5.5;
  });
  y += 6;

  // Quarterly
  doc.setFontSize(12); doc.setFont('helvetica','bold'); doc.setTextColor(30,30,30);
  doc.text('Quarterly Breakdown', 20, y); y += 8;
  doc.setFillColor(248,248,245); doc.rect(20, y-4, pageW-40, 7, 'F');
  doc.setFontSize(7.5); doc.setFont('helvetica','bold'); doc.setTextColor(100,100,100);
  doc.text('QUARTER', 25, y); doc.text('REVENUE', 100, y); doc.text('% OF TOTAL', 140, y); y += 7;
  doc.setFont('helvetica','normal'); doc.setFontSize(9); doc.setTextColor(50,50,50);
  ['Q1','Q2','Q3','Q4'].forEach((label, i) => {
    doc.text(label, 25, y); doc.text(formatCurrency(q[i]), 100, y);
    doc.text((s.total_revenue ? Math.round(q[i]/s.total_revenue*100) : 0) + '%', 140, y); y += 5.5;
  });
  doc.setFont('helvetica','bold');
  doc.text('TOTAL', 25, y); doc.text(formatCurrency(s.total_revenue), 100, y); y += 8;

  // Client Summary
  doc.setFontSize(12); doc.setTextColor(30,30,30);
  doc.text('Client Summary', 20, y); y += 8;
  doc.setFillColor(248,248,245); doc.rect(20, y-4, pageW-40, 7, 'F');
  doc.setFontSize(7); doc.setFont('helvetica','bold'); doc.setTextColor(100,100,100);
  doc.text('CLIENT', 25, y); doc.text('TYPE', 80, y); doc.text('COUNTRY', 105, y); doc.text('TAX ID', 130, y); doc.text('PAID', pageW-25, y, {align:'right'}); y += 7;
  doc.setFont('helvetica','normal'); doc.setFontSize(8); doc.setTextColor(50,50,50);
  (data.clients||[]).forEach((c:any) => {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.text((c.name||'').substring(0,30), 25, y);
    doc.text(c.type === 'business' ? 'Business' : 'Individual', 80, y);
    doc.text((c.country||'\u2014').substring(0,10), 105, y);
    doc.text((c.tax_id||c.vat_number||'\u2014').substring(0,18), 130, y);
    doc.text(formatCurrency(c.total_paid), pageW-25, y, {align:'right'}); y += 5;
  });
  y += 6; if (y > 240) { doc.addPage(); y = 20; }

  // Invoice detail
  doc.setFontSize(12); doc.setFont('helvetica','bold'); doc.setTextColor(30,30,30);
  doc.text('Invoice Detail', 20, y); y += 8;
  doc.setFillColor(248,248,245); doc.rect(20, y-4, pageW-40, 7, 'F');
  doc.setFontSize(7); doc.setFont('helvetica','bold'); doc.setTextColor(100,100,100);
  doc.text('INVOICE', 25, y); doc.text('CLIENT', 55, y); doc.text('COUNTRY', 105, y); doc.text('STATUS', 130, y); doc.text('TOTAL', 155, y); doc.text('PAID DATE', pageW-25, y, {align:'right'}); y += 7;
  doc.setFont('helvetica','normal'); doc.setFontSize(8); doc.setTextColor(50,50,50);
  (data.invoices||[]).forEach((i:any) => {
    if (y > 275) { doc.addPage(); y = 20; }
    doc.text(i.invoice_number||'', 25, y);
    doc.text((i.client_name||'').substring(0,25), 55, y);
    doc.text((i.client_country||'\u2014').substring(0,10), 105, y);
    doc.text(i.status||'', 130, y);
    doc.text(formatCurrency(i.total), 155, y);
    doc.text(i.paid_at ? formatDate(i.paid_at) : '\u2014', pageW-25, y, {align:'right'}); y += 5;
  });

  // Stablecoin & Digital Asset Compliance
  y += 6; if (y > 270) { doc.addPage(); y = 20; }
  doc.setFontSize(12); doc.setFont('helvetica','bold'); doc.setTextColor(30,30,30);
  doc.text('Stablecoin & Digital Asset Compliance', 20, y); y += 8;

  const sc = data.stablecoin_summary || {};
  const complianceRows = [
    ['USDT Received (FMV)', formatCurrency(sc.total_usdt_received || 0)],
    ['USDC Received (FMV)', formatCurrency(sc.total_usdc_received || 0)],
    ['Total FMV at Receipt', formatCurrency(sc.total_fmv_at_receipt || 0)],
  ];
  doc.setFontSize(9); doc.setFont('helvetica','normal');
  complianceRows.forEach(([label, val]) => {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.setTextColor(80,80,80); doc.text(label, 25, y);
    doc.setTextColor(30,30,30); doc.setFont('helvetica','bold');
    doc.text(val, pageW-25, y, {align:'right'});
    doc.setFont('helvetica','normal'); y += 5.5;
  });
  y += 6;

  // FMV Records table
  if ((data.fmv_records||[]).length) {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.setFontSize(10); doc.setFont('helvetica','bold'); doc.setTextColor(30,30,30);
    doc.text('FMV Records', 20, y); y += 7;
    doc.setFillColor(248,248,245); doc.rect(20, y-4, pageW-40, 7, 'F');
    doc.setFontSize(7); doc.setFont('helvetica','bold'); doc.setTextColor(100,100,100);
    doc.text('INVOICE', 25, y); doc.text('CLIENT', 55, y); doc.text('CURRENCY', 100, y); doc.text('AMOUNT', 125, y); doc.text('FMV/UNIT', 150, y); doc.text('DATE', pageW-25, y, {align:'right'}); y += 7;
    doc.setFont('helvetica','normal'); doc.setFontSize(8); doc.setTextColor(50,50,50);
    (data.fmv_records||[]).forEach((r:any) => {
      if (y > 275) { doc.addPage(); y = 20; }
      doc.text(r.invoice_number||'', 25, y);
      doc.text((r.client_name||'').substring(0,22), 55, y);
      doc.text(r.crypto_currency||'', 100, y);
      doc.text(r.amount_crypto != null ? String(r.amount_crypto) : '', 125, y);
      doc.text(formatCurrency(r.fmv_usd_per_unit), 150, y);
      doc.text(r.fetched_at ? formatDate(r.fetched_at) : '\u2014', pageW-25, y, {align:'right'}); y += 5;
    });
    y += 4;
  }

  // Form 8300 Filing Tracker
  if ((data.form8300_filings||[]).length) {
    if (y > 270) { doc.addPage(); y = 20; }
    doc.setFontSize(10); doc.setFont('helvetica','bold'); doc.setTextColor(30,30,30);
    doc.text('Form 8300 Filing Tracker', 20, y); y += 7;
    doc.setFillColor(248,248,245); doc.rect(20, y-4, pageW-40, 7, 'F');
    doc.setFontSize(7); doc.setFont('helvetica','bold'); doc.setTextColor(100,100,100);
    doc.text('CLIENT', 25, y); doc.text('AMOUNT', 75, y); doc.text('DATE', 110, y); doc.text('DEADLINE', 140, y); doc.text('STATUS', pageW-25, y, {align:'right'}); y += 7;
    doc.setFont('helvetica','normal'); doc.setFontSize(8); doc.setTextColor(50,50,50);
    (data.form8300_filings||[]).forEach((f:any) => {
      if (y > 275) { doc.addPage(); y = 20; }
      doc.text((f.client_name||'').substring(0,25), 25, y);
      doc.text(formatCurrency(f.trigger_amount_usd), 75, y);
      doc.text(f.trigger_date ? formatDate(f.trigger_date) : '\u2014', 110, y);
      doc.text(f.filing_deadline ? formatDate(f.filing_deadline) : '\u2014', 140, y);
      doc.text(f.status||'pending', pageW-25, y, {align:'right'}); y += 5;
    });
    y += 4;
  }

  // FMV source note
  if (y > 270) { doc.addPage(); y = 20; }
  doc.setFontSize(7.5); doc.setFont('helvetica','italic'); doc.setTextColor(120,120,120);
  doc.text('FMV source: Coinbase (SEC-regulated exchange). Per IRS Notice 2014-21.', 20, y);
  y += 6;

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 15;
  doc.setDrawColor(220,220,220); doc.line(20, footerY-5, pageW-20, footerY-5);
  doc.setFontSize(7); doc.setTextColor(160,160,160);
  doc.text('Gravant Labs LLC | EIN: 35-2952235 | gravantlabs.com', pageW/2, footerY, {align:'center'});
  doc.text('This report is for informational purposes. Consult your tax advisor for filing.', pageW/2, footerY+4, {align:'center'});

  doc.save('tax-report-' + data.year + '.pdf');
  toast('PDF exported', 'success');
}

// ─── Settings ───────────────────────────────────────────────────────────────
export async function renderSettings() {
  const content = $('#content')!;
  setBreadcrumb([{label:'Dashboard',href:'/dashboard'},{label:'Settings'}]);
  const u = state.user!;
  // SAFE: user data escaped via esc()
  setHTML(content, '<div class="page-enter"><div class="page-header"><h1>Settings</h1></div>'
    + '<div class="settings-tabs"><button class="settings-tab active" data-tab="profile">Profile</button><button class="settings-tab" data-tab="security">Security</button>'+(isAdmin()?'<button class="settings-tab" data-tab="portal">Portal</button>':'')+'</div>'
    + '<div id="settings-content">'
    + '<div class="settings-section" id="tab-profile"><div class="card"><div class="card-header"><h3>Profile</h3></div><div class="card-body"><form id="profile-form">'
    + '<div class="form-row"><div class="form-group"><label>Name</label><input type="text" name="name" value="'+esc(u.name)+'"></div><div class="form-group"><label>Email</label><input type="email" value="'+esc(u.email)+'" disabled></div></div>'
    + '<div class="form-row"><div class="form-group"><label>Company</label><input type="text" name="company" value="'+esc(u.company||'')+'"></div><div class="form-group"><label>Phone</label><input type="text" name="phone" value="'+esc(u.phone||'')+'"></div></div>'
    + '<div class="form-row"><div class="form-group"><label>Country</label><input type="text" name="country" value="'+esc(u.country||'')+'"></div><div class="form-group"><label>Address</label><input type="text" name="address" value="'+esc(u.address||'')+'"></div></div>'
    + '<button type="submit" class="btn btn-primary mt-2">Save</button></form></div></div></div>'
    + '<div class="settings-section" id="tab-security" style="display:none"><div class="card"><div class="card-header"><h3>Change Password</h3></div><div class="card-body"><form id="password-form">'
    + '<div class="form-group"><label>Current Password</label><input type="password" name="current_password" required></div>'
    + '<div class="form-row"><div class="form-group"><label>New Password</label><input type="password" name="new_password" required minlength="8"></div><div class="form-group"><label>Confirm</label><input type="password" id="confirm-pw" required minlength="8"></div></div>'
    + '<button type="submit" class="btn btn-primary mt-2">Update Password</button></form></div></div></div>'
    + (isAdmin()?'<div class="settings-section" id="tab-portal" style="display:none"><div class="card"><div class="card-header"><h3>Portal</h3></div><div class="card-body"><div class="detail-grid">'
    + '<div class="detail-item"><div class="detail-label">Company</div><div class="detail-value">Gravant Labs LLC</div></div>'
    + '<div class="detail-item detail-full"><div class="detail-label">Address</div><div class="detail-value">1201 Orange Street, Suite 600, Wilmington, DE 19801</div></div>'
    + '</div><p class="text-muted mt-2" style="font-size:0.85rem">API keys configured via wrangler.toml env vars</p></div></div></div>':'')
    + '</div></div>');
  icons();
  $$('.settings-tab').forEach(tab => { tab.addEventListener('click', () => { $$('.settings-tab').forEach(t=>t.classList.remove('active')); tab.classList.add('active'); $$('.settings-section').forEach((s:any)=>s.style.display='none'); const tgt=document.getElementById('tab-'+tab.getAttribute('data-tab')); if(tgt)(tgt as HTMLElement).style.display='block'; }); });
  document.getElementById('profile-form')?.addEventListener('submit', async (e) => { e.preventDefault(); const fd=new FormData(e.target as HTMLFormElement); const body=Object.fromEntries(fd); try { const d=await api.put('/auth/profile',body); state.user={...state.user!,...d.user}; localStorage.setItem('gl_user',JSON.stringify(state.user)); document.getElementById('user-name')!.textContent=state.user!.name; toast('Profile updated','success'); } catch(err:any){toast(err.message,'error');} });
  document.getElementById('password-form')?.addEventListener('submit', async (e) => { e.preventDefault(); const fd=new FormData(e.target as HTMLFormElement); const body:any=Object.fromEntries(fd); if(body.new_password!==(document.getElementById('confirm-pw') as HTMLInputElement).value){toast('Passwords do not match','error');return;} try { await api.put('/auth/password',body); toast('Password updated','success'); (e.target as HTMLFormElement).reset(); } catch(err:any){toast(err.message,'error');} });
}
